import {deburr, get, toLower} from "lodash";
import i18n from "i18next";
import {CustomDetector} from "i18next-browser-languagedetector";
import {IArticles, IPlayer, ISquad, NullOrEmpty} from "modules/types";
import {Language, RequestStateType} from "modules/enums";
import {gameZoneLogos, headerLogos, headerLogosMobile, IMAGES_URL, logos} from "modules/constants";
import emptyPlayer from "assets/images/emptyPlayer.svg";

export function withTimeStamp(path: string) {
	const t = new Date().getTime().toString();
	return `${path}?t=${t}`;
}

export function firstLetter(name: string) {
	if (!name) {
		return "";
	}

	return name.charAt(0);
}

export function toLowerCase(path: string) {
	if (!path) {
		return "";
	}
	return path.toLowerCase();
}

export function getDashedValue(value: NullOrEmpty<string | number>) {
	if (value === null || value === undefined) {
		return "-";
	}

	return String(value);
}

export function parseStateType(state: RequestStateType) {
	const isIdle = state === RequestStateType.Idle;
	const isLoading = state === RequestStateType.Loading;
	const isSuccess = state === RequestStateType.Success;
	const isError = state === RequestStateType.Error;
	const isLoadingOrSuccess = isLoading || isSuccess;

	return {isIdle, isLoading, isSuccess, isError, isLoadingOrSuccess};
}

export function parseRequestState(state: RequestStateType) {
	const isIdle = state === RequestStateType.Idle;
	const isLoading = state === RequestStateType.Loading;
	const isComplete = state === RequestStateType.Success || state === RequestStateType.Error;

	return {isIdle, isLoading, isComplete, requestState: state};
}

export const getLang = (): string => {
	const currentLang = i18n.language;
	return currentLang || "en";
};

export const getHelpJsonPath = (): string => {
	const currentLang = i18n.language;
	return currentLang === "en" ? "en-us/" : "fr-ca/";
};

export const getHeaderLogo = (lang: string) =>
	get(headerLogos, lang, headerLogos[Language.English]);

export const getHeaderLogoMobile = (lang: string) =>
	get(headerLogosMobile, lang, headerLogosMobile[Language.English]);

export const getAllStarLogo = (lang: string) => get(logos, lang, logos[Language.English]);

export const getPlayerAvatarSrc = (player: IPlayer) => {
	return player.image ? `${IMAGES_URL}${player.image}` : emptyPlayer;
};

export const getPlayerName = (player: IPlayer) => {
	return `${player.firstName.charAt(0)}. ${player.lastName}`;
};

export const getPlayerFullName = (player: IPlayer) => {
	return `${player.firstName} ${player.lastName}`;
};

export const getArticlesData = (articles: IArticles, player: IPlayer, lang: string) => {
	const playerArticles = get(articles, player.id, []);

	return playerArticles.map((article) => {
		const title = get(article.title, lang, "") as string;
		const desc = get(article.text, lang, "") as string;
		const src = get(article, "image", "");
		const link = get(article.url, lang, "") as string;

		return {
			title,
			desc,
			src,
			link,
		};
	});
};

export const getVideoArticleData = (player: IPlayer, lang: string) => {
	const title = get(player?.videoTitle, lang, "") as string;
	const src = get(player, "videoUrl", "") as string;

	if (!title || !src) {
		return;
	}

	return {
		title,
		src,
	};
};

export const getTeamFieldHasNews = (articles: IArticles, player: IPlayer, lang: string) => {
	const hasArticle = Boolean(getArticlesData(articles, player, lang).length);
	const hasVideoArticle = Boolean(getVideoArticleData(player, lang));
	return hasArticle || hasVideoArticle;
};

export function createMarkup(html: string) {
	return {__html: html};
}

export const languageDetectorByUrl = {
	name: "languageDetectorByUrl",

	lookup() {
		if (window.location.origin.includes("lcf")) {
			return Language.French;
		}

		return Language.English;
	},
} as CustomDetector;

export const getGameZoneLogo = (lang: string) =>
	get(gameZoneLogos, lang, gameZoneLogos[Language.English]);

export const getSquadKey = (squad?: ISquad) => {
	if (!squad) {
		return "";
	}

	return `squad.name.${squad.abbreviation.toLowerCase()}`;
};

export const numberWithCommas = (x: number = 0) => x.toLocaleString("en-US");

export const buildHelpPathName = (name: string) => {
	return `/help/${toLower(deburr(name)).split(" ").join("-")}`;
};
